import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import WizardCst from "@components/WizardCst";
import {
  WizardStep,
  WizardStepProps,
  ValidationResult,
} from "@components/WizardCst/types";
import { DynamicGroupWizardData, hasValidRuleSets } from "./types";
import DynGroupInfoStep from "./steps/DynGroupInfoStep";
import RulesStep from "./steps/RulesStep";
import EnrollStep from "../GroupWizard/steps/EnrollStep";
import EnrollmentModeStep from "../GroupWizard/steps/EnrollmentModeStep";
import { skipEnrollMode, validateEnrollMode } from "../GroupWizard/validation";

const validateInfo = (data: DynamicGroupWizardData): ValidationResult => {
  const errors: Record<string, string> = {};
  if (!data.name?.trim()) errors.name = "required";
  if (!data.description?.trim()) errors.description = "required";
  return { valid: Object.keys(errors).length === 0, errors };
};

const validateRules = (data: DynamicGroupWizardData): ValidationResult => {
  const ok = hasValidRuleSets(data.sets);
  return { valid: ok, errors: ok ? {} : { sets: "rules-required" } };
};

interface DynamicGroupWizardProps {
  initialData: DynamicGroupWizardData;
  backTo: string;
  onComplete: (data: DynamicGroupWizardData) => void | Promise<void>;
  isCompleting?: boolean;
  title?: string;
  abandonMode?: "create" | "edit";
}

const DynamicGroupWizard: React.FC<DynamicGroupWizardProps> = ({
  initialData,
  backTo,
  onComplete,
  isCompleting,
  title,
  abandonMode = "create",
}) => {
  const { t } = useTranslation("group-wizard-v2");

  const steps = useMemo<WizardStep<DynamicGroupWizardData>[]>(
    () => [
      {
        key: "main-info",
        label: t("steps.mainInfo"),
        Component: DynGroupInfoStep,
        validate: validateInfo,
      },
      {
        key: "rules",
        label: t("steps.rules"),
        Component: RulesStep,
        validate: validateRules,
        canProceed: (data) => hasValidRuleSets(data.sets),
      },
      {
        key: "enroll",
        label: t("steps.enroll"),
        Component: (props: WizardStepProps<DynamicGroupWizardData>) => (
          <EnrollStep {...props} />
        ),
      },
      {
        key: "enroll-mode",
        label: t("steps.enrollMode"),
        Component: (props: WizardStepProps<DynamicGroupWizardData>) => (
          <EnrollmentModeStep
            {...props}
            hiddenEnrollTypes={
              abandonMode === "edit" ? undefined : ["AUTO", "BOTTOM"]
            }
          />
        ),
        validate: validateEnrollMode,
        skip: skipEnrollMode,
      },
    ],
    [t, abandonMode],
  );

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <WizardCst<DynamicGroupWizardData>
          title={title ?? t("titleCreateDynamic")}
          breadcrumb={<Breadcrumb sticky={false} />}
          backLink={{ label: t("backLink"), to: backTo }}
          steps={steps}
          initialData={initialData}
          onComplete={onComplete}
          abandonMode={abandonMode}
          navigationMode={abandonMode === "edit" ? "free" : "sequential"}
          abandonCopy={{
            title: String(
              t(
                abandonMode === "edit"
                  ? "abandon.edit-title"
                  : "abandon.create-title",
              ),
            ),
            description:
              abandonMode === "edit"
                ? undefined
                : String(t("abandon.create-description")),
          }}
          nextLabel={t("next")}
          completeLabel={t("confirm")}
          backLabel={t("back")}
          resetLabel={t("reset")}
          isCompleting={isCompleting}
          resetConfirm={{
            title: t("resetConfirm.title"),
            description: t("resetConfirm.description"),
            confirmLabel: t("resetConfirm.confirm"),
            cancelLabel: t("resetConfirm.cancel"),
          }}
        />
      </Box>
    </AnimationFadeIn>
  );
};

export default DynamicGroupWizard;
